import { Icon } from '@natera/material/lib/icon'
import { useRef, useState } from 'react'
import { cn } from '~/shared/lib/cn'
import { useHorizontalScroll } from '~/shared/lib/use-horizontal-scroll'
import { IconButton, ScrollArea, Text } from '~/shared/ui'
import { computePaidAmount } from '../../../lib'
import type { PatientClaimFormApi, PatientClaimFormValues } from '../../../model'
import { AllowedAmountCell } from './allowed-amount-cell'
import { CarcRarcTaggingSheet } from './carc-rarc-tagging'
import { CurrencyCell } from './currency-cell'
import { DeductionColumn } from './deduction-column'
import { ServiceDateCell } from './service-date-cell'

const thClass = 'p-2 text-left font-normal border-divider border-r'
const thGroupClass = 'p-2 text-center font-normal border-divider border-r border-b bg-surface-low'
const thSubClass = 'p-1 px-2 text-left font-normal border-divider border-r bg-surface-low'
const tdClass =
  'border-divider border-b border-r p-2 focus-within:shadow-[inset_0_0_0_1px_var(--color-primary-main)]'
const inputClass =
  'text-text-primary w-full border-none bg-transparent px-0 py-1 text-[15px] leading-5 tracking-[0.25px] outline-none'

const taggingSheetColumnLabels = {
  adjustments: 'Adjustments',
  deductible: 'Deductible',
  coInsurance: 'Co-insurance',
  copayment: 'Copayment',
  nonCovered: 'Non-Covered',
} as const

interface ServiceLinesTableProps {
  form: PatientClaimFormApi
  serviceLineCount: number
  onRemove: (index: number) => void
  setHistoryValues: (values: Partial<PatientClaimFormValues>) => void
}

export function ServiceLinesTable({
  form,
  serviceLineCount,
  onRemove,
  setHistoryValues,
}: ServiceLinesTableProps) {
  const recordServiceLines = () =>
    setHistoryValues({ serviceLines: form.state.values.serviceLines })
  const scrollRef = useRef<HTMLDivElement>(null)
  const { canScrollLeft, canScrollRight } = useHorizontalScroll(scrollRef)

  // CARC/RARC tagging sheet state
  const [taggingSheetOpen, setTaggingSheetOpen] = useState(false)
  const [taggingSheetIndex, setTaggingSheetIndex] = useState<number>(0)
  const [taggingSheetField, setTaggingSheetField] = useState<
    'adjustments' | 'deductible' | 'coInsurance' | 'copayment' | 'nonCovered'
  >('adjustments')

  if (serviceLineCount === 0) return null

  const stickyClass = 'sticky right-0 z-[1]'
  const shadowClass = canScrollRight
    ? 'before:pointer-events-none before:absolute before:inset-y-0 before:right-[calc(100%+1px)] before:w-2 before:bg-linear-to-r before:from-transparent before:to-black/6'
    : ''

  return (
    <div className="relative">
      {canScrollLeft && (
        <div className="pointer-events-none absolute inset-y-0 left-0 z-2 w-2 bg-linear-to-l from-transparent to-black/6" />
      )}
      <ScrollArea
        orientation="horizontal"
        viewportRef={scrollRef}
        viewportClassName="w-0 min-w-full"
      >
        <table className="w-full border-separate border-spacing-0">
          <thead>
            {/* Row 1: top-level groups */}
            <tr className="bg-surface-low">
              <th
                className={cn(thClass, 'min-w-[88px]')}
                rowSpan={2}
              >
                <Text appearance="BODY2">Service Date</Text>
              </th>
              <th
                className={cn(thClass, 'min-w-20')}
                rowSpan={2}
              >
                <Text appearance="BODY2">CPT Code</Text>
              </th>
              <th
                className={cn(thClass, 'min-w-[75px]')}
                rowSpan={2}
              >
                <Text appearance="BODY2">Billed Amount</Text>
              </th>
              <th
                className={cn(thGroupClass)}
                colSpan={2}
              >
                <Text appearance="BODY2">Payor Deductions</Text>
              </th>
              <th
                className={cn(thClass, 'min-w-[75px]')}
                rowSpan={2}
              >
                <Text appearance="BODY2">Allowed Amount</Text>
              </th>
              <th
                className={cn(thGroupClass, 'border-r-0')}
                colSpan={3}
              >
                <Text appearance="BODY2">Patient Responsibility</Text>
              </th>
              <th
                className={cn(
                  'border-divider bg-surface-medium max-w-32 min-w-32 border-l py-2 pr-3 pl-2 text-right font-normal whitespace-nowrap',
                  stickyClass,
                  shadowClass,
                )}
                rowSpan={2}
              >
                <Text appearance="BODY2">Paid Amount</Text>
              </th>
            </tr>
            {/* Row 2: sub-columns */}
            <tr className="bg-surface-low">
              <th className={cn(thSubClass, 'min-w-[140px]')}>
                <Text
                  className="whitespace-nowrap"
                  appearance="CAPTION"
                >
                  Adjustments &amp; Discounts
                </Text>
              </th>
              <th className={cn(thSubClass, 'min-w-[85px]')}>
                <Text appearance="CAPTION">Other Policy</Text>
              </th>
              <th className={cn(thSubClass, 'min-w-[85px]')}>
                <Text appearance="CAPTION">Deductible</Text>
              </th>
              <th className={cn(thSubClass, 'min-w-[134px]')}>
                <Text appearance="CAPTION">Co-insurance</Text>
              </th>
              <th className={cn(thSubClass, 'min-w-[85px] border-r-0')}>
                <Text appearance="CAPTION">Copayment</Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: serviceLineCount }, (_, i) => (
              <tr
                key={i}
                className="group"
              >
                <td className={tdClass}>
                  <form.Field name={`serviceLines[${i}].serviceDate`}>
                    {(field) => (
                      <ServiceDateCell
                        value={field.state.value}
                        onValueChange={(v) => {
                          field.handleChange(v)
                          recordServiceLines()
                        }}
                      />
                    )}
                  </form.Field>
                </td>
                <td className={tdClass}>
                  <form.Field name={`serviceLines[${i}].cptCode`}>
                    {(field) => (
                      <input
                        className={inputClass}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={recordServiceLines}
                      />
                    )}
                  </form.Field>
                </td>
                <td className={tdClass}>
                  <form.Field name={`serviceLines[${i}].billedAmount`}>
                    {(field) => (
                      <CurrencyCell
                        value={field.state.value}
                        onCentsChange={(cents) => field.handleChange(cents)}
                        onBlur={recordServiceLines}
                      />
                    )}
                  </form.Field>
                </td>
                <DeductionColumn
                  form={form}
                  index={i}
                  field="adjustments"
                  recordServiceLines={recordServiceLines}
                  onOpenSheet={() => {
                    setTaggingSheetIndex(i)
                    setTaggingSheetField('adjustments')
                    setTaggingSheetOpen(true)
                  }}
                />
                <td className={tdClass}>
                  <form.Field name={`serviceLines[${i}].otherPlan`}>
                    {(field) => (
                      <CurrencyCell
                        value={field.state.value}
                        onCentsChange={(cents) => field.handleChange(cents)}
                        onBlur={recordServiceLines}
                      />
                    )}
                  </form.Field>
                </td>
                <AllowedAmountCell
                  form={form}
                  index={i}
                  recordServiceLines={recordServiceLines}
                />
                <DeductionColumn
                  form={form}
                  index={i}
                  field="deductible"
                  recordServiceLines={recordServiceLines}
                  onOpenSheet={() => {
                    setTaggingSheetIndex(i)
                    setTaggingSheetField('deductible')
                    setTaggingSheetOpen(true)
                  }}
                />
                <DeductionColumn
                  form={form}
                  index={i}
                  field="coInsurance"
                  recordServiceLines={recordServiceLines}
                  onOpenSheet={() => {
                    setTaggingSheetIndex(i)
                    setTaggingSheetField('coInsurance')
                    setTaggingSheetOpen(true)
                  }}
                  showAmountTypeToggle
                />
                <DeductionColumn
                  form={form}
                  index={i}
                  field="copayment"
                  recordServiceLines={recordServiceLines}
                  onOpenSheet={() => {
                    setTaggingSheetIndex(i)
                    setTaggingSheetField('copayment')
                    setTaggingSheetOpen(true)
                  }}
                  className="border-r-0"
                />
                <td
                  className={cn(
                    'border-divider bg-surface-lowest max-w-32 min-w-32 border-b border-l py-2 pr-3 pl-2 whitespace-nowrap',
                    stickyClass,
                    shadowClass,
                  )}
                >
                  <form.Subscribe selector={(state) => state.values.serviceLines[i]}>
                    {(serviceLine) => {
                      const cents = serviceLine ? computePaidAmount(serviceLine) : 0
                      const formatted = (cents / 100).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                      return (
                        <div className="flex items-center justify-between gap-1">
                          <Icon
                            children="attach_money"
                            className="text-text-secondary text-[18px]"
                          />
                          <div className="flex items-center gap-0.5">
                            <Text appearance="BODY2">{formatted}</Text>
                            {serviceLineCount > 1 && (
                              <IconButton
                                className="opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
                                size="small"
                                container="small"
                                materialIcon="delete"
                                onClick={() => onRemove(i)}
                              />
                            )}
                          </div>
                        </div>
                      )
                    }}
                  </form.Subscribe>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollArea>

      {/* Single CARC/RARC tagging sheet instance shared across all rows */}
      <form.Subscribe
        selector={(state) => {
          const d = state.values.serviceLines[taggingSheetIndex]?.[taggingSheetField]
          return {
            codeGroups: d?.codeGroups ?? [],
            amountType: d?.amountType ?? ('cents' as const),
          }
        }}
      >
        {({ codeGroups, amountType }) => (
          <CarcRarcTaggingSheet
            open={taggingSheetOpen}
            onOpenChange={setTaggingSheetOpen}
            initialCodeGroups={codeGroups}
            columnLabel={taggingSheetColumnLabels[taggingSheetField]}
            deductionField={taggingSheetField}
            amountType={amountType}
            onSave={(newCodeGroups, summedAmount) => {
              form.setFieldValue(
                `serviceLines[${taggingSheetIndex}].${taggingSheetField}`,
                (prev) => ({
                  ...prev,
                  codeGroups: newCodeGroups,
                  // Empty sidebar amounts + empty cell → default to 0 so save isn't a no-op.
                  amount: summedAmount ?? prev.amount ?? 0,
                }),
              )
              recordServiceLines()
            }}
          />
        )}
      </form.Subscribe>
    </div>
  )
}
